
import Vue, { PropType } from "vue";
import { getHygienistProfile, updateTalentEducationCredentials } from "@/api";
import {
  EducationTalent, EducationForm, credentialMonths, credentialFieldLabel,
  canMarkNotApplicable, educationForm, educationUpdate, validateEducationForm
} from "@/helpers/educationCredentials";

export default Vue.extend({
  props: {
    value: Boolean,
    talent: { type: Object as PropType<EducationTalent | null>, default: null }
  },
  data() {
    return {
      form: { graduationYear: "", firstLicensedYear: "", firstLicensedMonth: null, notApplicable: false } as EducationForm,
      current: null as EducationTalent | null,
      error: "", saving: false, conflict: false, refreshing: false, refreshRequestId: 0, months: credentialMonths
    };
  },
  computed: {
    fieldLabel(): string { return this.current ? credentialFieldLabel(this.current) : ""; },
    allowNotApplicable(): boolean { return !!this.current && canMarkNotApplicable(this.current.type); }
  },
  watch: {
    value(open: boolean) {
      this.refreshRequestId++;
      this.refreshing = false;
      if (open && this.talent) this.reset(this.talent);
    }
  },
  beforeDestroy() { this.refreshRequestId++; },
  methods: {
    reset(talent: EducationTalent) {
      this.current = { ...talent };
      this.form = educationForm(talent);
      this.error = ""; this.conflict = false;
    },
    close() {
      if (this.saving) return;
      this.refreshRequestId++;
      this.refreshing = false;
      this.$emit("input", false);
    },
    async refresh() {
      if (!this.current) return;
      const talent = { ...this.current };
      const requestId = ++this.refreshRequestId;
      this.refreshing = true;
      try {
        const data = await getHygienistProfile(talent.id);
        if (requestId !== this.refreshRequestId || !this.value || this.current?.id !== talent.id) return;
        const refreshed = { ...talent, ...data };
        this.reset(refreshed);
        this.$emit("refreshed", refreshed);
      } catch (error) {
        if (requestId === this.refreshRequestId) this.error = "Could not refresh the profile. Please try again.";
      } finally {
        if (requestId === this.refreshRequestId) this.refreshing = false;
      }
    },
    async save() {
      if (!this.current || this.saving || this.conflict) return;
      this.error = validateEducationForm(this.form, this.current);
      if (this.error) return;
      this.saving = true;
      try {
        const result = await updateTalentEducationCredentials(this.current.id, educationUpdate(this.form, this.current));
        this.$emit("saved", { ...result, id: this.current.id });
        this.$emit("input", false);
      } catch (error) {
        const failure = error as Error & { status?: number };
        this.error = failure.message || "Could not save. Please try again.";
        this.conflict = failure.status === 409 || (failure.status || 0) >= 500;
      } finally { this.saving = false; }
    }
  }
});
